import React, { useState, useEffect, useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import LoadingButton from "@mui/lab/LoadingButton";
import { Button, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ProductLabels from "../../templates/print/ProductLabels50";

const ProductBarcodesDialog = ({ ToggleOpen, open, product }) => {
  const { enqueueSnackbar } = useSnackbar();
  const printRefBarcode = useRef();
  const [newInventoryValues, setNewInventoryValues] = useState({});
  const [inventoryErrors, setInventoryErrors] = useState({});
  const [proVarUpd, setProVarUpd] = useState(null);
  const [barcodePage, setBarcodePage] = useState(false);

  const totalInventory = useMemo(() => {
    return Object.values(newInventoryValues).reduce(
      (sum, value) => sum + (parseInt(value) || 0),
      0
    );
  }, [newInventoryValues]);
  const handleInventoryChange = (variantId, value) => {
    if (value === "" || (!isNaN(value) && parseInt(value) >= 0)) {
      setNewInventoryValues((prev) => ({ ...prev, [variantId]: value }));
      setInventoryErrors((prev) => ({ ...prev, [variantId]: false }));
    } else {
      setInventoryErrors((prev) => ({ ...prev, [variantId]: true }));
    }
  };

  useEffect(() => {
    setProVarUpd(product);
    setNewInventoryValues({});
    setInventoryErrors({});
  }, [product, ToggleOpen]);

  const printBarcodes = async () => {
    try {
      const inventoryUpdates = Object.entries(newInventoryValues)
        .filter(([_, value]) => !isNaN(value))
        .map(([var_id, quantity]) => ({
          variant_id: var_id,
          new_quantity: Number(quantity),
        }));

      if (inventoryUpdates.length === 0) {
        enqueueSnackbar("Blank inventory fields...", { variant: "warning" });
        return;
      }

      setProVarUpd((prevProduct) => ({
        ...prevProduct,
        product_title: prevProduct.title, // ensure `product_title` exists
        variants: prevProduct.variants.map((variant) => {
          const match = inventoryUpdates.find(
            (update) => update.variant_id == variant.id
          );
          if (match) {
            return {
              ...variant,
              bar_quantity: match.new_quantity,
            };
          }
          return variant;
        }),
      }));

      setBarcodePage(true);
    } catch (error) {
      enqueueSnackbar("Failed to generate barcodes", {
        variant: "error",
        autoHideDuration: 3000,
      });
      console.error("Barcode generation error:", error);
    }
  };

  const handleClose = () => {
    ToggleOpen(null);
    setBarcodePage(false);
  };
  const barcodePagePrint = useReactToPrint({
    content: () => printRefBarcode.current,
    documentTitle: "Barcodes",

    onAfterPrint: () => {
      setBarcodePage(false);
      ToggleOpen(null);
    },
  });
  if (!open) return null;

  if (barcodePage) {
    return (
      <div className="container">
        <div className="shadow p-1 bg-body rounded floating-button-top">
          <button
            className="btn btn-sm btn-info no-print"
            onClick={() => setBarcodePage(false)}
          >
            Back to Edit
          </button>
          <button
            className="ms-2 btn btn-sm btn-success"
            onClick={barcodePagePrint}
          >
            Print Barcodes
          </button>
        </div>

        <div ref={printRefBarcode}>
          {" "}
          {product && <ProductLabels data={proVarUpd} />}
        </div>
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="barcode-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="barcode-dialog-title">
        Print Barcodes for {product?.title || "Product"}
      </DialogTitle>

      <DialogContent dividers>
        {product?.variants?.map((variant) => (
          <TextField
            key={variant.id}
            label={variant.title}
            color="secondary"
            size="small"
            type="number"
            value={newInventoryValues[variant.id] || ""}
            onChange={(e) => handleInventoryChange(variant.id, e.target.value)}
            inputProps={{
              min: 0,
              step: 1,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            sx={{
              width: "100%",
              margin: "8px 0",
            }}
            error={inventoryErrors[variant.id]}
            helperText={
              inventoryErrors[variant.id] ? "Please enter a valid number" : ""
            }
            variant="outlined"
            fullWidth
          />
        ))}
        <p>
          Total: {totalInventory} {totalInventory === 1 ? "item" : "items"}
        </p>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <LoadingButton
          color="primary"
          onClick={printBarcodes}
          loadingPosition="start"
          startIcon={<PrintIcon />}
          variant="contained"
        >
          Generate Barcodes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ProductBarcodesDialog;
